import {
  Heart,
  Shirt,
  Dumbbell,
  Building2,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Mail,
} from "lucide-react";

const ZEFFY_LINK =
  "https://www.zeffy.com/donation-form/support-unified-hoops-league-uhl";

const fundedItems = [
  {
    icon: Shirt,
    title: "Jerseys",
    text: "Every player gets a free UHL jersey to call their own.",
  },
  {
    icon: Dumbbell,
    title: "Equipment",
    text: "Basketballs, training gear, and everything we need on the court.",
  },
  {
    icon: Building2,
    title: "Gym & facility costs",
    text: "Rental fees so we can host weekly sessions in safe, welcoming spaces.",
  },
  {
    icon: Sparkles,
    title: "Coaching & program supplies",
    text: "Cones, scoreboards, snacks, water — the little things that keep practices running.",
  },
  {
    icon: TrendingUp,
    title: "Future growth of the league",
    text: "Helping us welcome more players and expand into new communities.",
  },
];

const DonateButton = ({ size = "lg", testId, className = "" }) => {
  const base =
    "inline-flex items-center justify-center gap-3 bg-orange-500 hover:bg-orange-600 hover:-translate-y-0.5 hover:shadow-2xl transition-all font-black rounded-full text-white shadow-xl whitespace-nowrap";
  const sizing =
    size === "lg" ? "px-10 py-5 text-lg sm:text-xl" : "px-8 py-4 text-lg";
  return (
    <a
      href={ZEFFY_LINK}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`${base} ${sizing} ${className}`}
    >
      <Heart className="w-5 h-5 sm:w-6 sm:h-6" />
      Donate to UHL
    </a>
  );
};

export default function Donate() {
  return (
    <div data-testid="donate-page">
      {/* HERO */}
      <section className="relative overflow-hidden basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10 md:pt-24 md:pb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 text-orange-700 text-sm font-extrabold uppercase tracking-wider">
            <Heart className="w-4 h-4" />
            Support UHL
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Help us keep basketball{" "}
            <span className="text-blue-600">free for every family.</span>
          </h1>
          <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600 max-w-2xl">
            UHL is completely free for every player — but jerseys, equipment,
            and gym time aren't. Your donation directly supports the kids on
            our court and helps us grow.
          </p>
        </div>
      </section>

      {/* DONATE CARD */}
      <section className="pb-8">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div
            data-testid="donate-card"
            className="bg-slate-900 rounded-3xl p-8 sm:p-10 md:p-12 text-white shadow-2xl shadow-blue-900/20"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-orange-500/15 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-orange-400" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-300">
                  Donate to UHL
                </p>
                <h2 className="mt-1 text-2xl sm:text-3xl font-black leading-tight">
                  Every dollar lands directly on the court.
                </h2>
                <p className="mt-3 text-slate-300 font-medium leading-relaxed">
                  Click the button below to open our secure donation form.
                  Choose any amount — every contribution helps fund jerseys,
                  equipment, and gym time for our players.
                </p>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <DonateButton testId="donate-cta-btn" />
              <p className="flex items-start gap-2 text-sm text-slate-400 font-semibold">
                <ShieldCheck className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                Secure donations are processed through our nonprofit
                partner, PAASS.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FUNDRAISING LETTER */}
      <section
        className="py-16 md:py-24 bg-white"
        data-testid="fundraising-letter"
      >
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
            A note from the founders
          </p>
          <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
            Why your support matters.
          </h2>

          <div className="mt-6 space-y-5 text-slate-700 text-lg font-medium leading-relaxed">
            <p>
              UHL exists because every kid deserves a team — especially kids
              who have often been left on the sidelines. We started this
              program after years of volunteering with kids with intellectual
              and developmental disabilities, and we kept seeing the same
              thing: their faces light up when they get to play, but the
              opportunities to keep playing are too few.
            </p>
            <p>
              We made a promise that UHL would always be free for families.
              No registration fees. No equipment fees. No "pay-to-play."
              Just basketball, friendship, and a place to belong.
            </p>
            <p>
              Every dollar you donate goes directly toward making that
              promise possible — and helps us welcome more players in the
              future. Thank you for being part of our community.
            </p>
            <p className="font-bold text-slate-900">
              — Taylor Katz & Julian MacColl, Co-Founders of UHL
            </p>
          </div>
        </div>
      </section>

      {/* WHAT YOUR DONATION FUNDS */}
      <section
        className="py-16 md:py-24 bg-[#FAFAFA]"
        data-testid="donation-impact"
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-3xl">
            <p className="text-sm font-black uppercase tracking-[0.2em] text-orange-500">
              Where your donation goes
            </p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Every dollar lands directly on the court.
            </h2>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {fundedItems.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                data-testid={`fund-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="bg-white rounded-3xl p-7 border border-slate-100 shadow-xl shadow-blue-900/5 hover:-translate-y-1 hover:shadow-2xl transition-all"
              >
                <div className="w-12 h-12 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="mt-5 text-lg font-black text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed text-sm">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="bg-slate-900 rounded-3xl p-10 md:p-14 text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-black">
              Ready to support UHL?
            </h2>
            <p className="mt-3 text-slate-300 font-medium text-lg max-w-2xl mx-auto">
              Open our secure donation form and choose any amount that feels
              right. Every dollar helps.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <DonateButton testId="donate-bottom-cta" size="md" />
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Question%20about%20supporting%20UHL"
                data-testid="donate-questions-btn"
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-100 transition-all font-bold px-8 py-4 rounded-full text-lg"
              >
                <Mail className="w-5 h-5" />
                Questions? Email us
              </a>
            </div>
            <p className="mt-5 flex items-center justify-center gap-2 text-sm text-slate-400 font-semibold">
              <ShieldCheck className="w-4 h-4 text-green-400" />
              Secure donations are processed through our nonprofit partner,
              PAASS.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
